import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { cn } from '../../../../utils/utils';

let nextFieldId = 0;

/** The first real form control projected into the field, if any. */
const CONTROL_SELECTOR =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select, textarea';

/**
 * When no form control is projected (a picker button, chips with an Add
 * button…), the label goes to the first match of these, in this order.
 * Form controls always come first, so ngx-mat-input-tel still gets its tel
 * input rather than its country button.
 */
const FALLBACK_SELECTORS = [
  '[role="combobox"]',
  '[aria-haspopup]:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

/**
 * Labelled frame around a projected control (input, select, textarea,
 * ngx-mat-input-tel, chips…). Label above, the frame draws fill, border,
 * hover, focus and error; the message sits below in normal flow.
 * Visual rules live in the "Form fields (bk-field)" block of styles.css so
 * new forms can use the same classes without this component.
 */
@Component({
  selector: 'input-field',
  standalone: true,
  imports: [],
  templateUrl: './inputField.component.html',
  styleUrl: './inputField.component.css',
})
export class InputFieldComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) label = '';
  @Input() errorMessage = '';
  @Input() labelClass = '';
  /** Helper text under the field. Hidden while an error is showing. */
  @Input() hint = '';
  merge = cn;

  readonly fieldId = `bk-field-${nextFieldId++}`;
  readonly labelId = `${this.fieldId}-label`;
  readonly messageId = `${this.fieldId}-message`;
  private readonly controlId = `${this.fieldId}-control`;

  @ViewChild('controlEl', { static: true })
  private controlEl!: ElementRef<HTMLElement>;

  private renderer = inject(Renderer2);
  private zone = inject(NgZone);
  /** ARIA wiring runs in the browser only; the server render stays plain. */
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private viewReady = false;
  /** The element the ARIA attributes were last written to. */
  private linked: HTMLElement | null = null;
  /** ARIA attributes are only cleared when this component set them. */
  private ownsLabelledBy = false;
  private ownsInvalid = false;
  private ownsId = false;
  /** Re-links when the projected control is rendered later (@if, @for…). */
  private observer?: MutationObserver;

  ngAfterViewInit(): void {
    this.viewReady = this.isBrowser;
    if (!this.viewReady) return;
    this.linkControl();

    const host = this.controlEl?.nativeElement;
    if (host && typeof MutationObserver !== 'undefined') {
      // Outside Angular: re-linking only writes attributes, so the DOM changes
      // inside the slot do not need a change-detection pass of their own.
      this.zone.runOutsideAngular(() => {
        this.observer = new MutationObserver(() => {
          if (this.findControl() !== this.linked) this.linkControl();
        });
        this.observer.observe(host, { childList: true, subtree: true });
      });
    }
  }

  ngOnChanges(): void {
    if (this.viewReady) this.linkControl();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /** Clicking the label focuses the control, as a native label would. */
  focusControl(): void {
    this.findControl()?.focus();
  }

  /**
   * Ties the projected control to this label and message for assistive tech:
   * aria-labelledby (by id, so repeated controls with the same id in a list
   * still get their own label), aria-invalid while an error shows, and the
   * error or hint added to aria-describedby. A name the consumer gave the
   * control is left alone, and the consumer's own aria-describedby ids are
   * kept.
   */
  private linkControl(): void {
    const control = this.findControl();
    if (control !== this.linked) {
      if (this.linked?.isConnected) this.unlink(this.linked);
      this.linked = control;
      this.ownsLabelledBy = false;
      this.ownsInvalid = false;
      this.ownsId = false;
    }
    if (!control) return;

    const isFormControl = control.matches(CONTROL_SELECTOR);

    const namedByConsumer =
      !this.ownsLabelledBy &&
      (control.hasAttribute('aria-label') ||
        control.hasAttribute('aria-labelledby'));
    if (this.label && !namedByConsumer) {
      // A button keeps its own text in its name ("Organizations, 2 chosen"),
      // so it lists itself after the label.
      let labelledBy = this.labelId;
      if (!isFormControl) {
        if (!control.id) {
          this.renderer.setAttribute(control, 'id', this.controlId);
          this.ownsId = true;
        }
        labelledBy += ` ${control.id}`;
      }
      this.renderer.setAttribute(control, 'aria-labelledby', labelledBy);
      this.ownsLabelledBy = true;
    } else if (!this.label && this.ownsLabelledBy) {
      this.renderer.removeAttribute(control, 'aria-labelledby');
      this.ownsLabelledBy = false;
    }

    // aria-invalid belongs on form controls (and comboboxes), not buttons.
    if (this.errorMessage && (isFormControl || control.getAttribute('role') === 'combobox')) {
      this.renderer.setAttribute(control, 'aria-invalid', 'true');
      this.ownsInvalid = true;
    } else if (isFormControl || this.ownsInvalid) {
      this.renderer.removeAttribute(control, 'aria-invalid');
      this.ownsInvalid = false;
    }

    this.setDescribedBy(control, !!(this.errorMessage || this.hint));
  }

  /** Adds or removes this field's message id, keeping the consumer's ids. */
  private setDescribedBy(control: HTMLElement, show: boolean): void {
    const ids = (control.getAttribute('aria-describedby') ?? '')
      .split(/\s+/)
      .filter((id) => id && id !== this.messageId);
    if (show) ids.push(this.messageId);
    if (ids.length) {
      this.renderer.setAttribute(control, 'aria-describedby', ids.join(' '));
    } else {
      this.renderer.removeAttribute(control, 'aria-describedby');
    }
  }

  /** Takes back what this component wrote to a control it no longer labels. */
  private unlink(control: HTMLElement): void {
    if (this.ownsLabelledBy) this.renderer.removeAttribute(control, 'aria-labelledby');
    if (this.ownsInvalid) this.renderer.removeAttribute(control, 'aria-invalid');
    if (this.ownsId) this.renderer.removeAttribute(control, 'id');
    this.setDescribedBy(control, false);
  }

  private findControl(): HTMLElement | null {
    const host = this.controlEl?.nativeElement;
    if (!this.isBrowser || !host || typeof host.querySelector !== 'function') {
      return null;
    }
    const control = host.querySelector<HTMLElement>(CONTROL_SELECTOR);
    if (control) return control;
    for (const selector of FALLBACK_SELECTORS) {
      const fallback = host.querySelector<HTMLElement>(selector);
      if (fallback) return fallback;
    }
    return null;
  }
}
